/**
 * FacilityGaitPage — Facility-Level Gait Overview
 *
 * Layout: Thin summary panel on the left (total residents + severity filters),
 * 3 metric cards on the right (Steps, Stride, Balance).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import apiService from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import "../styles/pages/FacilityGaitPage.css";

/* ── Card Icons ── */

const StepsIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M13 5.5C13 3.57 11.43 2 9.5 2S6 3.57 6 5.5c0 1.38.81 2.56 2 3.11V20a1 1 0 1 0 2 0V8.61c1.19-.55 2-1.73 2-3.11z" />
    <path d="M19 10.5c0-1.93-1.57-3.5-3.5-3.5S12 8.57 12 10.5c0 1.38.81 2.56 2 3.11V20a1 1 0 1 0 2 0v-6.39c1.19-.55 2-1.73 2-3.11z" />
  </svg>
);

const StrideIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M4 18h16M4 18l4-4M20 18l-4-4" />
    <path d="M12 14V4" />
    <path d="M8 8l4-4 4 4" />
  </svg>
);

const BalanceIcon = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="4" r="2" />
    <path d="M12 6v8" />
    <path d="M6 14l6 6 6-6" />
    <path d="M4 20h16" />
  </svg>
);

/* ── Subcomponents ── */

function ResidentRow({ resident, onClick }) {
  return (
    <div
      className="facility-gait-resident-row"
      onClick={() => onClick(resident.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(resident.id); }}
    >
      <div className="facility-gait-resident-row__left">
        <span className="facility-gait-resident-row__name">{resident.name}</span>
        <span className="facility-gait-resident-row__room">
          {resident.room ? `Room ${resident.room}` : ""}
        </span>
      </div>
      <div className="facility-gait-resident-row__right">
        <span className="facility-gait-resident-row__value">
          {resident.todayValue} {resident.unit}
        </span>
        <span className={`facility-gait-resident-row__decline facility-gait-resident-row__decline--${resident.severity}`}>
          ↓ {resident.percentDecline}%
        </span>
        <span className={`facility-gait-resident-row__badge facility-gait-resident-row__badge--${resident.severity}`} />
      </div>
    </div>
  );
}

function MetricCard({ title, icon: Icon, residents, onResidentClick }) {
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const listRef = useRef(null);

  useEffect(() => {
    const calculateItems = () => {
      if (!listRef.current) return;
      const listHeight = listRef.current.clientHeight;
      const rowHeight = 46; // row height (padding + content + gap)
      const fits = Math.max(1, Math.floor(listHeight / rowHeight));
      setItemsPerPage(fits);
    };
    calculateItems();
    window.addEventListener("resize", calculateItems);
    return () => window.removeEventListener("resize", calculateItems);
  }, []);

  useEffect(() => { setPage(0); }, [residents]);

  const totalPages = Math.ceil(residents.length / itemsPerPage);
  const startIdx = page * itemsPerPage;
  const visibleResidents = residents.slice(startIdx, startIdx + itemsPerPage);

  return (
    <div className="facility-gait-card">
      <div className="facility-gait-card__header">
        <div className="facility-gait-card__header-left">
          <div className="facility-gait-card__icon"><Icon /></div>
          <span className="facility-gait-card__title">{title}</span>
        </div>
        <span className="facility-gait-card__count">{residents.length} flagged</span>
      </div>
      <div className="facility-gait-card__body">
        {residents.length === 0 ? (
          <div className="facility-gait-card__empty">
            <span className="facility-gait-card__empty-text">All within normal range ✓</span>
          </div>
        ) : (
          <>
            <div className="facility-gait-card__list" ref={listRef}>
              {visibleResidents.map((r) => (
                <ResidentRow key={r.id} resident={r} onClick={onResidentClick} />
              ))}
            </div>
            {totalPages > 1 && (
              <div className="facility-gait-card__pagination">
                <button
                  className="facility-gait-card__pagination-btn"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  aria-label="Previous page"
                >
                  <svg viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
                <span className="facility-gait-card__pagination-info">{page + 1} / {totalPages}</span>
                <button
                  className="facility-gait-card__pagination-btn"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  aria-label="Next page"
                >
                  <svg viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" /></svg>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Main Component ── */

export default function FacilityGaitPage({ onResidentClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getFacilityGaitOverview();
      setData(result);
    } catch (err) {
      console.error("[FacilityGaitPage] Failed to load overview:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFilterClick = (severity) => {
    setActiveFilter((current) => (current === severity ? null : severity));
  };

  const filterResidents = (residents) => {
    if (!activeFilter) return residents;
    return residents.filter((r) => r.severity === activeFilter);
  };

  const handleResidentClick = (residentId) => {
    if (onResidentClick) onResidentClick(residentId);
  };

  if (loading) {
    return (
      <main className="facility-gait-page--loading">
        <LoadingSpinner message="Loading gait overview..." />
      </main>
    );
  }

  if (error && !data) {
    return (
      <main className="facility-gait-page--error">
        <div className="facility-gait-card__error">
          <span className="facility-gait-card__error-text">Failed to load facility gait overview</span>
          <button className="facility-gait-card__error-retry" onClick={loadData}>Retry</button>
        </div>
      </main>
    );
  }

  const stepsAll = data?.steps || [];
  const strideAll = data?.stride || [];
  const balanceAll = data?.balance || [];

  const allEntries = [...stepsAll, ...strideAll, ...balanceAll];
  const counts = {
    attention: allEntries.filter((r) => r.severity === "attention").length,
    monitor: allEntries.filter((r) => r.severity === "monitor").length,
    observe: allEntries.filter((r) => r.severity === "observe").length,
  };

  // Count unique residents across all cards
  const uniqueResidentIds = new Set([
    ...stepsAll.map(r => r.id),
    ...strideAll.map(r => r.id),
    ...balanceAll.map(r => r.id),
  ]);
  const totalResidentsShown = uniqueResidentIds.size;

  return (
    <main className="facility-gait-page">
      {/* ── Left Summary Panel (thin, same height as cards) ── */}
      <div className="facility-gait-summary-panel">
        {/* Total residents card */}
        <div className="facility-gait-summary-panel__card">
          <div className="facility-gait-summary-panel__card-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <span className="facility-gait-summary-panel__card-value">{totalResidentsShown}</span>
          <span className="facility-gait-summary-panel__card-label">Residents Flagged</span>
        </div>

        {/* Needs Attention */}
        <button
          className={`facility-gait-summary-panel__card facility-gait-summary-panel__card--clickable ${activeFilter === "attention" ? "facility-gait-summary-panel__card--active-attention" : ""}`}
          onClick={() => handleFilterClick("attention")}
        >
          <span className="facility-gait-summary-panel__card-value">{counts.attention}</span>
          <div className="facility-gait-summary-panel__card-label-row">
            <div className="facility-gait-summary-panel__card-dot facility-gait-summary-panel__card-dot--attention" />
            <span className="facility-gait-summary-panel__card-label">Needs Attention</span>
          </div>
        </button>

        {/* Monitor */}
        <button
          className={`facility-gait-summary-panel__card facility-gait-summary-panel__card--clickable ${activeFilter === "monitor" ? "facility-gait-summary-panel__card--active-monitor" : ""}`}
          onClick={() => handleFilterClick("monitor")}
        >
          <span className="facility-gait-summary-panel__card-value">{counts.monitor}</span>
          <div className="facility-gait-summary-panel__card-label-row">
            <div className="facility-gait-summary-panel__card-dot facility-gait-summary-panel__card-dot--monitor" />
            <span className="facility-gait-summary-panel__card-label">Monitor</span>
          </div>
        </button>

        {/* Watch List */}
        <button
          className={`facility-gait-summary-panel__card facility-gait-summary-panel__card--clickable ${activeFilter === "observe" ? "facility-gait-summary-panel__card--active-observe" : ""}`}
          onClick={() => handleFilterClick("observe")}
        >
          <span className="facility-gait-summary-panel__card-value">{counts.observe}</span>
          <div className="facility-gait-summary-panel__card-label-row">
            <div className="facility-gait-summary-panel__card-dot facility-gait-summary-panel__card-dot--observe" />
            <span className="facility-gait-summary-panel__card-label">Watch List</span>
          </div>
        </button>
      </div>

      {/* ── Metric Cards (right, flex: 1) ── */}
      <div className="facility-gait-cards-container">
        <MetricCard
          title="Steps"
          icon={StepsIcon}
          residents={filterResidents(stepsAll)}
          onResidentClick={handleResidentClick}
        />
        <MetricCard
          title="Stride"
          icon={StrideIcon}
          residents={filterResidents(strideAll)}
          onResidentClick={handleResidentClick}
        />
        <MetricCard
          title="Balance"
          icon={BalanceIcon}
          residents={filterResidents(balanceAll)}
          onResidentClick={handleResidentClick}
        />
      </div>
    </main>
  );
}
